import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  CssBaseline,
  useTheme,
  useMediaQuery,
  Fab,
  IconButton,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import Sidebar from './Sidebar';
import CustomerBanner from '../Common/CustomerBanner';
import { useWebPhoneContext } from '../../contexts/WebPhoneContext';
import { WebPhoneWindow } from '../WebPhone';

const DRAWER_WIDTH = 240;

const Layout: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const { isWindowOpen, isMinimized, closeWindow, minimizeWindow } = useWebPhoneContext();

  const handleDrawerToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Redirect mobile users to phone on initial login - DISABLED
  // VM3 is not ready yet, will enable later
  // useEffect(() => {
  //   const mobilePhoneShown = sessionStorage.getItem('mobilePhoneShown');
  //
  //   // If mobile and haven't shown phone yet, redirect to mobile phone
  //   // Check for both / and /voicemail since / redirects to /voicemail
  //   if (isMobile && !mobilePhoneShown && (location.pathname === '/' || location.pathname === '/voicemail')) {
  //     sessionStorage.setItem('mobilePhoneShown', 'true');
  //     navigate('/webphone-mobile-v3');
  //   }
  // }, [isMobile, location.pathname, navigate]);

  // Handle customer view mode to set document title
  useEffect(() => {
    // Check localStorage for customer view state
    const checkCustomerView = () => {
      try {
        const stored = localStorage.getItem('loginAsCustomer');
        if (stored) {
          const state = JSON.parse(stored);
          document.title = `${state.customerId} - ${state.customerName}`;
        }
      } catch (error) {
        // Ignore errors
      }
    };

    // Check on mount and when storage changes
    checkCustomerView();

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'loginAsCustomer') {
        checkCustomerView();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        width={DRAWER_WIDTH}
      />
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2 },
          width: { md: `calc(100% - ${sidebarOpen ? DRAWER_WIDTH : 0}px)` },
          ml: { md: sidebarOpen ? 0 : `-${DRAWER_WIDTH}px` },
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          minHeight: '100vh',
          backgroundColor: theme.palette.background.default,
          position: 'relative',
        }}
      >
        {/* Desktop menu button when sidebar is closed */}
        {!isMobile && !sidebarOpen && (
          <IconButton
            onClick={handleDrawerToggle}
            sx={{
              position: 'fixed',
              left: 8,
              top: 16,
              zIndex: theme.zIndex.speedDial,
              backgroundColor: theme.palette.background.paper,
              boxShadow: 2,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        )}
        
        <CustomerBanner />
        <Outlet />
      </Box>
      
      {/* Floating menu button for mobile */}
      {isMobile && !sidebarOpen && (
        <Fab
          color="primary"
          aria-label="menu"
          onClick={handleDrawerToggle}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: theme.zIndex.speedDial,
          }}
        >
          <MenuIcon />
        </Fab>
      )}

      {/* WebPhone Window - stays floating across pages */}
      {isWindowOpen && (
        <WebPhoneWindow onClose={closeWindow} onMinimize={minimizeWindow} isMinimized={isMinimized} />
      )}
    </Box>
  );
};

export default Layout;